import { useCreateProductCategory, useProductCategories } from '@/api';
import { Button, FormSelect, Input } from '@/components/atoms';
import { FormField } from '@/components/molecules';
import {
  productCategorySchema,
  type ProductCategoryFormData,
} from '@/validation-schema';
import type { ProductCategory } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';

interface CategoryCreateFormProps {
  onBack?: () => void;
  onCancel?: () => void;
  onSuccess?: (result: { categoryId: number; subcategoryId: number }) => void;
  showBackButton?: boolean;
}

export const CategoryCreateForm = ({
  onBack,
  onCancel,
  onSuccess,
  showBackButton = false,
}: CategoryCreateFormProps) => {
  const [subcategoryInput, setSubcategoryInput] = useState('');
  const [subcategories, setSubcategories] = useState<string[]>([]);

  const { data: categories = [] } = useProductCategories();

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<ProductCategoryFormData>({
    resolver: yupResolver(
      productCategorySchema
    ) as Resolver<ProductCategoryFormData>,
    mode: 'onChange',
    defaultValues: {
      categoryName: '',
      existingCategoryId: '',
    },
  });

  const existingCategoryId = watch('existingCategoryId');

  useEffect(() => {
    if (!existingCategoryId) return;
    const selected = categories.find(
      (category) => String(category.id) === existingCategoryId
    );
    if (selected) {
      setValue('categoryName', selected.name, { shouldValidate: true });
    }
  }, [categories, existingCategoryId, setValue]);

  const { mutate: saveCategory, isPending } = useCreateProductCategory();

  const handleAddSubcategory = () => {
    const trimmed = subcategoryInput.trim();
    if (!trimmed) return;
    if (
      subcategories.some((item) => item.toLowerCase() === trimmed.toLowerCase())
    ) {
      setSubcategoryInput('');
      return;
    }
    setSubcategories((prev) => [...prev, trimmed]);
    setSubcategoryInput('');
  };

  const handleRemoveSubcategory = (name: string) => {
    setSubcategories((prev) => prev.filter((item) => item !== name));
  };

  const submit = (data: ProductCategoryFormData) => {
    if (subcategories.length === 0) return;

    const pendingSubcategories = [...subcategories];

    saveCategory(
      {
        name: data.existingCategoryId ? undefined : data.categoryName.trim(),
        subcategories: pendingSubcategories,
        categoryId: data.existingCategoryId
          ? Number(data.existingCategoryId)
          : undefined,
      },
      {
        onSuccess: ({ data: category }) => {
          if (!category) return;

          const matchedSubcategory = [...pendingSubcategories]
            .reverse()
            .map((name) =>
              category.subcategories.find(
                (subcategory) =>
                  subcategory.name.toLowerCase() === name.toLowerCase()
              )
            )
            .find(Boolean);

          const subcategoryId =
            matchedSubcategory?.id ??
            category.subcategories[category.subcategories.length - 1]?.id;

          if (subcategoryId) {
            onSuccess?.({
              categoryId: category.id,
              subcategoryId,
            });
          }

          reset();
          setSubcategoryInput('');
          setSubcategories([]);
          onBack?.();
        },
      }
    );
  };

  const categoryOptions = categories.map((category: ProductCategory) => ({
    value: String(category.id),
    label: category.name,
  }));

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
      {showBackButton && onBack && (
        <Button
          type="button"
          variant="ghost"
          className="w-fit -ml-2 gap-1.5"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" />
          Back to categories
        </Button>
      )}

      <FormField
        label="Existing category (optional)"
        error={errors.existingCategoryId?.message}
      >
        <div className="flex gap-2">
          <div className="flex-1">
            <Controller
              name="existingCategoryId"
              control={control}
              render={({ field }) => (
                <FormSelect
                  placeholder="Select to add subcategories"
                  value={field.value ?? ''}
                  onChange={(value) => {
                    field.onChange(value);
                    if (!value) {
                      setValue('categoryName', '', {
                        shouldValidate: true,
                      });
                    }
                  }}
                  options={categoryOptions}
                />
              )}
            />
          </div>
          {existingCategoryId && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setValue('existingCategoryId', '', { shouldValidate: true });
                setValue('categoryName', '', { shouldValidate: true });
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </FormField>

      <FormField label="Category name" error={errors.categoryName?.message}>
        <Input
          placeholder="e.g. Electronics"
          {...register('categoryName')}
          disabled={!!existingCategoryId}
        />
      </FormField>

      <FormField label="Subcategory name">
        <div className="flex gap-2">
          <Input
            placeholder="e.g. Phones"
            value={subcategoryInput}
            onChange={(event) => setSubcategoryInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddSubcategory();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleAddSubcategory}
            disabled={!subcategoryInput.trim()}
          >
            <Plus className="size-4" />
            Add
          </Button>
        </div>
      </FormField>

      {subcategories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {subcategories.map((name) => (
            <Button
              key={name}
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => handleRemoveSubcategory(name)}
            >
              {name}
              <X className="size-3.5" />
            </Button>
          ))}
        </div>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          label="Save"
          isLoading={isPending}
          disabled={!isValid || subcategories.length === 0}
        />
      </div>
    </form>
  );
};
